import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GameResultOverlay from './GameResultOverlay';
import useGameSounds from './useGameSounds';

const TURN_SECONDS = 60;
const SUIT_GLYPH = { spades: '♠', hearts: '♥', diamonds: '♦', clubs: '♣' };

function Card({ rank, suit, hidden }) {
    if (hidden) {
        return (
            <div className="w-10 h-14 sm:w-14 sm:h-20 rounded border-2 border-cyber-purple/60 text-cyber-purple flex items-center justify-center font-display bg-cyber-purple/10 shrink-0 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage:
                            'repeating-linear-gradient(45deg, transparent 0 4px, rgba(177,75,255,0.35) 4px 6px)',
                    }}
                />
                <span className="relative text-2xl sm:text-3xl font-black glow-purple">?</span>
            </div>
        );
    }
    const isRed = suit === 'hearts' || suit === 'diamonds';
    const colorClass = isRed ? 'border-cyber-pink text-cyber-pink' : 'border-cyber-cyan text-cyber-cyan';
    return (
        <div className={`w-10 h-14 sm:w-14 sm:h-20 rounded border-2 flex flex-col items-center justify-center font-display bg-black/70 ${colorClass} shrink-0`}>
            <span className="text-sm sm:text-lg font-bold leading-none">{rank}</span>
            <span className="text-lg sm:text-2xl leading-none mt-0.5">{SUIT_GLYPH[suit] || '?'}</span>
        </div>
    );
}

function HandRow({ hand }) {
    if (!hand || hand.length === 0) {
        return <div className="h-14 sm:h-20 text-cyber-dim text-xs tracking-widest flex items-center">—</div>;
    }
    return (
        <div className="flex flex-wrap gap-1 sm:gap-2">
            {hand.map((c, i) => <Card key={i} rank={c.rank} suit={c.suit} hidden={c.hidden} />)}
        </div>
    );
}

function BlackjackRoom({ player, opponent, socket, bet, blackjackState, blackjackUpdate, blackjackEnd, setBlackjackUpdate }) {
    const { t } = useTranslation();
    const [secondsLeft, setSecondsLeft] = useState(TURN_SECONDS);
    const [showResult, setShowResult] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const { playWin, playLose, playChoose, playDraw } = useGameSounds();
    const lastUpdateKeyRef = useRef(null);

    const myName = player.name;
    const oppName = opponent;

    const myHand = blackjackState?.hands?.[myName] || [];
    const oppHand = blackjackState?.hands?.[oppName] || [];
    const myScore = blackjackState?.scores?.[myName] ?? 0;
    const oppScoreRaw = blackjackState?.scores?.[oppName];
    const oppScoreHidden = oppScoreRaw === null || oppScoreRaw === undefined;
    const oppScore = oppScoreHidden ? 0 : oppScoreRaw;
    const iStood = !!blackjackState?.stood?.[myName];
    const iBust = !!blackjackState?.busted?.[myName];
    const oppStood = !!blackjackState?.stood?.[oppName];
    const oppBust = !!blackjackState?.busted?.[oppName];
    const canAct = !!blackjackState && !iStood && !iBust && !blackjackEnd;

    // Feedback sound on opponent's action
    useEffect(() => {
        if (!blackjackUpdate) return;
        const key = `${blackjackUpdate.actor}:${blackjackUpdate.action}:${oppHand.length}`;
        if (lastUpdateKeyRef.current === key) return;
        lastUpdateKeyRef.current = key;
        if (blackjackUpdate.actor && blackjackUpdate.actor !== myName) {
            playChoose();
        }
    }, [blackjackUpdate]); // eslint-disable-line

    // Reset countdown on any turn transition or round (re)start
    useEffect(() => {
        if (!blackjackState || blackjackEnd) return;
        if (!canAct) return;
        setSecondsLeft(TURN_SECONDS);
        const interval = setInterval(() => {
            setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(interval);
    }, [blackjackState?.hands?.[myName]?.length, blackjackState?.hands?.[oppName]?.length, iStood, iBust, oppStood, oppBust, blackjackEnd]); // eslint-disable-line

    // End-of-round overlay
    useEffect(() => {
        if (!blackjackEnd) {
            setShowResult(false);
            setGameResult(null);
            return;
        }
        let result;
        if (blackjackEnd.winnerName === null || blackjackEnd.winnerName === undefined) {
            result = 'draw';
            playDraw();
        } else if (blackjackEnd.winnerName === myName) {
            result = 'win';
            playWin();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
        } else {
            result = 'lose';
            playLose();
            if (navigator.vibrate) navigator.vibrate(200);
        }
        setGameResult(result);
        setShowResult(true);
    }, [blackjackEnd]); // eslint-disable-line

    const sendAction = (action) => {
        if (!canAct) return;
        playChoose();
        if (navigator.vibrate) navigator.vibrate(30);
        socket.send(JSON.stringify({ eventName: 'make.choose', payload: { action } }));
    };

    const exitGame = () => {
        socket.send(JSON.stringify({ eventName: 'game.stop', payload: player.name }));
    };

    const statusBadge = (stood, bust) => {
        if (bust) return <span className="px-2 py-0.5 text-[10px] font-display tracking-widest bg-cyber-pink/20 text-cyber-pink border border-cyber-pink rounded glow-pink">{t('game.blackjack.bust')}</span>;
        if (stood) return <span className="px-2 py-0.5 text-[10px] font-display tracking-widest bg-cyber-purple/20 text-cyber-purple border border-cyber-purple rounded">{t('game.blackjack.stoodLabel')}</span>;
        return null;
    };

    const promptText = () => {
        if (blackjackEnd) return '';
        if (iBust) return t('game.blackjack.bust');
        if (iStood) return t('game.blackjack.waitingOpp');
        return t('game.blackjack.turnPrompt');
    };

    return (
        <div className="h-screen max-h-screen overflow-hidden p-2 sm:p-4 md:p-3">
            <div className="max-w-6xl mx-auto h-full flex flex-col space-y-3 sm:space-y-4 md:space-y-3">
                {/* Header */}
                <div className="neon-panel scanlines p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyber-cyan animate-pulse shadow-neon" />
                        <span className="font-display text-xs sm:text-sm tracking-widest text-cyber-cyan glow-cyan">{t('game.blackjack.title')}</span>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-center px-3 border-x border-cyber-border">
                            <div className="neon-label text-[10px]">{t('game.bet')}</div>
                            <div className="text-cyber-purple glow-purple font-display font-bold text-sm sm:text-base">{bet}¢</div>
                        </div>
                        <button onClick={exitGame} className="neon-btn neon-btn-pink text-xs">
                            {t('game.abortMatch')}
                        </button>
                    </div>
                </div>

                {/* Hands area */}
                <div className="neon-panel p-3 sm:p-4 relative overflow-hidden flex-1 min-h-0 flex flex-col">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {/* My hand */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <div className="neon-label">
                                    {t('game.blackjack.yourHand')} — <span className="text-cyber-cyan glow-cyan truncate">{myName}</span>
                                </div>
                                {statusBadge(iStood, iBust)}
                            </div>
                            <div className="text-cyber-cyan glow-cyan font-display text-lg sm:text-2xl font-black">
                                {t('game.blackjack.score', { n: myScore })}
                            </div>
                            <HandRow hand={myHand} />
                        </div>

                        {/* Opponent hand */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                                <div className="neon-label">
                                    {t('game.blackjack.opponentHand')} — <span className="text-cyber-pink glow-pink truncate">{oppName || '...'}</span>
                                </div>
                                {statusBadge(oppStood, oppBust)}
                            </div>
                            <div className="text-cyber-pink glow-pink font-display text-lg sm:text-2xl font-black">
                                {oppScoreHidden
                                    ? t('game.blackjack.score', { n: '?' })
                                    : t('game.blackjack.score', { n: oppScore })}
                            </div>
                            <HandRow hand={oppHand} />
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-4 relative">
                        {blackjackState && !blackjackEnd && (
                            <>
                                <div className={`text-xl sm:text-3xl font-display font-black tracking-widest ${canAct ? 'text-cyber-cyan glow-cyan' : 'text-cyber-dim'}`}>
                                    {promptText()}
                                </div>
                                {canAct && (
                                    <div className={`font-display text-lg sm:text-2xl ${secondsLeft <= 5 ? 'text-cyber-pink glow-pink animate-pulse' : 'text-cyber-dim'}`}>
                                        {t('game.blackjack.timer', { seconds: secondsLeft })}
                                    </div>
                                )}
                                {(oppBust || oppStood) && !blackjackEnd && (
                                    <div className="text-xs sm:text-sm text-cyber-purple tracking-widest">
                                        {oppBust ? t('game.blackjack.opponentBust') : t('game.blackjack.opponentStood')}
                                    </div>
                                )}
                                {blackjackUpdate?.autoStand && blackjackUpdate.actor === myName && (
                                    <div className="text-xs text-cyber-pink tracking-widest animate-pulse">
                                        {t('game.blackjack.autoStand')}
                                    </div>
                                )}
                            </>
                        )}
                        <GameResultOverlay result={gameResult} bet={bet} visible={showResult} />
                    </div>
                </div>

                {/* Action buttons */}
                <div className="neon-panel p-3 sm:p-4 flex-shrink-0">
                    <div className="grid grid-cols-2 gap-2 sm:gap-4 max-w-2xl mx-auto">
                        <button
                            onClick={() => sendAction('hit')}
                            disabled={!canAct}
                            className="neon-btn neon-btn-purple py-4 sm:py-5 text-sm sm:text-lg flex flex-col items-center gap-1 sm:gap-2"
                        >
                            <span className="text-2xl sm:text-3xl">➕</span>
                            <span>{t('game.blackjack.hit')}</span>
                        </button>
                        <button
                            onClick={() => sendAction('stand')}
                            disabled={!canAct}
                            className="neon-btn neon-btn-pink py-4 sm:py-5 text-sm sm:text-lg flex flex-col items-center gap-1 sm:gap-2"
                        >
                            <span className="text-2xl sm:text-3xl">✋</span>
                            <span>{t('game.blackjack.stand')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BlackjackRoom;
